import type { ReactNode } from 'react';
import { Link, Navigate, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useSessionReady } from '@/hooks/useSessionReady';
import { isFeatureOn } from '@/lib/appConfig';
import { isStoreBuild } from '@/lib/platform';
import { cn } from '@/lib/utils';
import { APP_BRAND, IS_APP_BUILD } from '@/lib/appBrand';
import { useAuthStore } from '@/stores/auth';

/**
 * Каркас гостевых экранов приложения: шапка с «Войти» и нижнее меню как у ученика.
 *
 * ⚠️ РЕШЕНИЕ ВЛАДЕЛЬЦА 14.09.2026 (договор docs/CONTRACTS/guest-mode.md): гость видит главную и
 * словарь, а «Обучение» и «Профиль» — личное, они ведут на регистрацию и после неё открываются.
 *
 * Сторожа входа — здесь, а не в роутере: каждый лишний компонент в `router.tsx` добавляет
 * предупреждение линтера. На сайте гостевых экранов нет — уводим на «/». Вошедшему здесь делать
 * нечего — в кабинет. Режим выключен с сервера — на экран входа.
 */
const ITEMS: { to: string; icon: string; labelKey: string; next?: string; end?: boolean }[] = [
  { to: '/guest', icon: 'home', labelKey: 'guest.navHome', end: true },
  { to: '/register', icon: 'school', labelKey: 'guest.navLearn', next: '/app/lessons' },
  { to: '/guest/vocabulary', icon: 'style', labelKey: 'guest.navVocab' },
  { to: '/register', icon: 'person', labelKey: 'guest.navProfile', next: '/app/profile' },
];

export function GuestShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const ready = useSessionReady();
  const user = useAuthStore((s) => s.user);
  const authed = useAuthStore((s) => Boolean(s.accessToken));

  if (!isStoreBuild()) return <Navigate to="/" replace />;
  if (!ready) return <div className="min-h-screen bg-background" aria-hidden />;
  if (authed && user) return <Navigate to="/app" replace />;
  if (!isFeatureOn('guest_mode')) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-background pb-28" style={{ paddingTop: 'var(--sa-top)' }}>
      <header className="mx-auto flex max-w-md items-center justify-between px-4 pt-4">
        {/* В store-сборке — APP_BRAND, OneBilim Academy с 1.1.1 (lib/appBrand.ts); сайт — прежний onebilim. */}
        <span className="font-display text-lg font-bold tracking-display text-on-surface">
          {IS_APP_BUILD ? APP_BRAND : 'onebilim'}
        </span>
        <Link
          to="/login"
          className="rounded-full bg-surface-container-low px-4 py-2 text-[13px] font-semibold text-on-surface"
        >
          {t('guest.signIn')}
        </Link>
      </header>

      <main className="mx-auto max-w-md space-y-4 px-4 pt-4">{children}</main>

      {/* Меню — та же панель, что у ученика (AppShell): гость узнаёт приложение и после входа. */}
      <nav
        className="app-dock fixed inset-x-0 bottom-0 z-30 rounded-t-[22px] bg-surface-container-lowest px-2 pt-1.5 shadow-[0_-4px_20px_rgba(36,31,27,0.10)]"
        style={{ paddingBottom: 'max(0.5rem, var(--sa-bottom))' }}
      >
        <div className="mx-auto flex max-w-md items-center justify-around">
          {ITEMS.map((item) => (
            <NavLink
              key={item.labelKey}
              to={item.to}
              end={item.end}
              state={item.next ? { next: item.next } : undefined}
              className={({ isActive }) =>
                cn(
                  'flex flex-1 flex-col items-center justify-center gap-0.5 rounded-pill py-2 text-[9.5px] font-semibold leading-tight transition',
                  isActive && item.to.startsWith('/guest')
                    ? 'bg-primary-container text-on-primary-container'
                    : 'text-on-surface-variant',
                )
              }
            >
              <span className="material-symbols-outlined text-[21px]">{item.icon}</span>
              {t(item.labelKey)}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
